<template>
  <div class="card">
    <div class="body queries-overview">
      <text-stats title="Visão geral das consultas" class="text-stats" :error="errors.cantFetchChartData">
        <item-text-stats
          :value="totalSuccess"
          name="Consultas com sucesso"
          class="item-text-stats"
          :loading="loading"
        />
        <item-text-stats
          :value="totalFailed"
          name="Consultas com erro"
          class="item-text-stats"
          :loading="loading"
        />
        <item-text-stats
          :value="`${successRate}%`"
          name="Taxa de sucesso"
          class="item-text-stats"
          :loading="loading"
        />
      </text-stats>

      <div class="chart">
        <area-chart-skeleton-loader
          :quantity-series="2"
          v-if="loading"
        />
        <apex-chart
          type="area"
          height="350"
          :options="chartOptions"
          :series="chartSeries"
          v-else
        ></apex-chart>
      </div>
    </div>
  </div>
</template>

<script>
import ApexChart from 'vue-apexcharts';
import TextStats from '@/metrics/components/period/queries-overview/TextStats.vue';
import ItemTextStats from '@/metrics/components/period/queries-overview/ItemTextStats.vue';
import AreaChartSkeletonLoader from '@/support/components/loader/AreaChartSkeletonLoader.vue';

export default {
  name: 'QueriesOverview',

  props: {
    period: {
      required: true,
      type: Number,
    },
  },

  data() {
    return {
      loading: false,
      totalSuccess: 0,
      totalFailed: 0,
      successRate: 0,
      chartSeries: [],
      chartOptionsXaxisCategories: [],
      errors: {
        cantFetchChartData: false,
      },
    };
  },

  methods: {
    /**
     * Fetch the chart data.
     *
     * @returns {Promise<void>}
     */
    async fetchChartData() {
      try {
        this.resetErrors();
        this.resetChartData();
        this.loading = true;

        const { data } = await this.$http.get(`metrics/charts/queries-overview?period=${this.period}`);

        this.chartSeries = [...data.series];
        this.chartOptionsXaxisCategories = [...data.categories];
        this.totalSuccess = data.totalSuccess;
        this.totalFailed = data.totalFailed;
        this.successRate = Math.round(data.successRate);
      } catch {
        this.errors.cantFetchChartData = true;
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Ocorreu um erro',
          text: 'Não foi possível carregar uma visão geral das consultas.',
        });
      } finally {
        this.loading = false;
      }
    },

    /**
     * Reset the errors to initial values.
     */
    resetErrors() {
      this.errors.cantFetchChartData = false;
    },

    /**
     * Reset chart data.
     */
    resetChartData() {
      this.totalSuccess = 0;
      this.totalFailed = 0;
      this.successRate = 0;
      this.chartSeries = [];
      this.chartOptionsXaxisCategories = [];
    },
  },

  computed: {
    chartOptions() {
      return {
        chart: {
          height: window.screen.availHeight > 768 ? 350 : 300,
          type: 'area',
          toolbar: {
            show: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'smooth',
        },
        xaxis: {
          type: 'date',
          categories: this.chartOptionsXaxisCategories,
          labels: {
            show: false,
          },
          tooltip: {
            enabled: false,
          },
        },
        markers: {
          strokeWidth: 3,
          colors: '#ffffff',
          strokeColors: ['#7366ff', '#f73164'],
          hover: {
            size: 6,
          },
        },
        yaxis: {
          low: 0,
          offsetX: 0,
          offsetY: 0,
          show: false,
          labels: {
            low: 0,
            offsetX: 0,
            show: false,
          },
          axisBorder: {
            low: 0,
            offsetX: 0,
            show: false,
          },
        },
        grid: {
          show: false,
          padding: {
            left: 0,
            right: 0,
            bottom: -15,
            top: -40,
          },
        },
        colors: ['#7366ff', '#f73164'],
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.5,
            stops: [0, 80, 100],
          },
        },
        legend: {
          show: false,
        },
        tooltip: {
          x: {
            format: 'dd/MM/yy',
          },
        },
      };
    },
  },

  watch: {
    period() {
      this.fetchChartData();
    },
  },

  mounted() {
    this.fetchChartData();
  },

  components: {
    ApexChart,
    TextStats,
    ItemTextStats,
    AreaChartSkeletonLoader,
  },
};
</script>

<style lang="scss" scoped>
.queries-overview {
  @apply flex;
  @apply gap-4;

  > .text-stats {
    @apply w-1/5;
  }

  > .text-stats .item-text-stats {
    @apply w-full;
  }

  > .chart {
    @apply w-4/5;
    @apply overflow-hidden;
    padding-bottom: 1.5rem;
  }
}
</style>
